import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiWrite, list } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { thaiDateTime } from '../lib/format'

// Contract from handlers_locks.go (part 118):
//   GET  /v1/locks                      -> Lock[]
//   POST /v1/locks                      -> Lock   (201, X-Lock-Token)
//   POST /v1/locks/{name}/release       -> { name, released, token }
// A lock is a lease with a fencing token. The token only ever increases per lock name; this
// screen shows it but the enforcement lives with whatever resource the lease protects.

interface LockRow {
  name: string
  owner: string
  token: number
  acquired_at: string
  expires_at: string
  heartbeat_at: string
  expired: boolean
}

const LOCKS_KEY = ['locks']

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

export function Locks() {
  const qc = useQueryClient()

  const [name, setName] = useState('')
  const [owner, setOwner] = useState('')
  const [ttl, setTtl] = useState('60')
  const [confirm, setConfirm] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const locks = useQuery({
    queryKey: LOCKS_KEY,
    queryFn: () => list<LockRow>('/v1/locks'),
    refetchInterval: 5_000,
    staleTime: 2_000,
  })

  const acquire = useMutation({
    mutationFn: () => apiWrite<LockRow>('/v1/locks', 'POST', {
      name: name.trim(),
      owner: owner.trim(),
      ttl_seconds: Number(ttl) || 0,
    }),
    onSuccess: (lk) => {
      setNotice(`ได้สิทธิ์ ${lk.name} แล้ว · token #${lk.token} · หมดอายุ ${thaiDateTime(lk.expires_at)}`)
      setName('')
      setConfirm(null)
      acquire.reset()
      void qc.invalidateQueries({ queryKey: LOCKS_KEY })
    },
  })

  const release = useMutation({
    mutationFn: (row: LockRow) => apiWrite<{ name: string; released: boolean; token: number }>(
      `/v1/locks/${encodeURIComponent(row.name)}/release`, 'POST',
      { owner: row.owner, token: row.token },
    ),
    onSuccess: (res) => {
      setNotice(`คืนสิทธิ์ ${res.name} แล้ว · token #${res.token} ถูกปิดใช้งาน`)
      setConfirm(null)
      void qc.invalidateQueries({ queryKey: LOCKS_KEY })
    },
    onError: () => {
      setConfirm(null)
      void qc.invalidateQueries({ queryKey: LOCKS_KEY })
    },
  })

  const rows = locks.data ?? []
  const expiredCount = rows.filter((row) => row.expired).length
  const maxToken = rows.reduce((max, row) => Math.max(max, row.token), 0)
  const canAcquire = name.trim().length > 0 && !acquire.isPending

  const head = (
    <PageHead
      title="ล็อกแบบกระจายและ fencing token"
      sub="สิทธิ์การถือครองระดับชื่อสำหรับงานที่ต้องมีผู้ทำเพียงรายเดียว · ทุกการได้สิทธิ์จะได้ token ที่เพิ่มขึ้นเสมอ · ผู้อ่านปลายทางต้องปฏิเสธ token ที่ต่ำกว่าค่าสูงสุดที่เคยเห็น"
    />
  )

  if (locks.isLoading) {
    return (
      <>
        {head}
        <div className="stack"><Skel h={110} /><Skel h={280} /></div>
      </>
    )
  }

  if (locks.isError) {
    return (
      <>
        {head}
        <Empty title="โหลดรายการล็อกไม่ได้" body="ลองรีเฟรชหน้าอีกครั้ง หรือตรวจสอบการเชื่อมต่อกับเซิร์ฟเวอร์" />
      </>
    )
  }

  return (
    <>
      {head}

      <div className="grid-4 stagger">
        <Stat label="ล็อกที่ถืออยู่" value={rows.length} unit="รายการ"
          foot="นับเฉพาะชื่อที่มีผู้ถือสิทธิ์อยู่จริง" />
        <Stat label="หมดอายุแล้ว" value={expiredCount} unit="รายการ"
          foot="ยังไม่ถูกคืนหรือ sweep · token ยังคงอยู่" />
        <Stat label="Token สูงสุด" value={maxToken} unit=""
          foot="ค่าที่ผู้อ่านปลายทางต้องจดจำไว้ใช้ตรวจการเขียนที่มาช้า" />
        <Stat label="อายุเริ่มต้น" value={60} unit="วินาที"
          foot="ปรับได้ต่อคำขอ · ต่ำสุด 5 วินาที สูงสุด 24 ชั่วโมง" />
      </div>

      <div className="grid-main" style={{ marginTop: 16 }}>
        <div className="card card-pad">
          <div className="row" style={{ flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div className="card-label">ล็อกในองค์กรนี้</div>
              <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                รีเฟรชทุก 5 วินาที · แสดง {rows.length} รายการ
              </div>
            </div>
            <button
              className="btn btn-sm"
              style={{ marginLeft: 'auto' }}
              onClick={() => void locks.refetch()}
              disabled={locks.isFetching}
            >
              {locks.isFetching ? 'กำลังโหลด…' : 'รีเฟรช'}
            </button>
          </div>

          <div className="rule" style={{ margin: '14px 0 0' }} />

          {rows.length === 0 ? (
            <div style={{ marginTop: 14 }}>
              <Empty
                title="ยังไม่มีล็อกที่ถูกถือครอง"
                body="เมื่อผู้ปฏิบัติงานขอสิทธิ์ชื่อใด ระบบจะแสดงชื่อ ผู้ถือสิทธิ์ token และเวลาหมดอายุที่นี่"
              />
            </div>
          ) : (
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="table data">
                <thead>
                  <tr>
                    <th>ชื่อล็อก</th>
                    <th>ผู้ถือสิทธิ์</th>
                    <th style={{ width: 90 }}>Token</th>
                    <th>เริ่มถือ</th>
                    <th>หมดอายุ</th>
                    <th style={{ width: 100 }}>สถานะ</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const pending = release.isPending && release.variables?.name === row.name
                    return (
                      <tr key={row.name} style={{ cursor: 'default' }}>
                        <td className="mono">{row.name}</td>
                        <td>
                          <div>{row.owner}</div>
                          <div className="dim mono" style={{ fontSize: 10.5, marginTop: 3 }}>
                            heartbeat {thaiDateTime(row.heartbeat_at)}
                          </div>
                        </td>
                        <td className="mono tnum">#{row.token}</td>
                        <td className="dim" style={{ whiteSpace: 'nowrap' }}>{thaiDateTime(row.acquired_at)}</td>
                        <td className="dim" style={{ whiteSpace: 'nowrap' }}>{thaiDateTime(row.expires_at)}</td>
                        <td>
                          <span
                            className={`chip${row.expired ? '' : ' gold'}`}
                            style={{
                              whiteSpace: 'nowrap',
                              color: row.expired ? 'var(--color-amber)' : 'var(--color-gold)',
                              borderColor: 'currentColor',
                            }}
                            title={row.expired
                              ? 'สิทธิ์หมดอายุแล้ว แต่ยังไม่ได้คืน · token ยังถูกเก็บไว้'
                              : 'ผู้ถือสิทธิ์ยังทำงานอยู่ภายในอายุสิทธิ์'}
                          >
                            {row.expired ? 'หมดอายุ' : 'ถือครองอยู่'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {confirm === row.name ? (
                            <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                              <button className="btn btn-sm" disabled={pending} onClick={() => release.mutate(row)}>
                                {pending ? 'กำลังคืน…' : 'ยืนยัน'}
                              </button>
                              <button className="btn btn-sm btn-ghost" onClick={() => setConfirm(null)}>ยกเลิก</button>
                            </div>
                          ) : (
                            <button className="btn btn-sm" onClick={() => { setNotice(null); setConfirm(row.name) }}>
                              คืนสิทธิ์
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {release.isError ? (
            <div className="form-error" style={{ marginTop: 12 }}>
              {errorText(release.error, 'คืนสิทธิ์ไม่สำเร็จ')}
            </div>
          ) : null}
        </div>

        <div className="card card-pad">
          <div className="card-label">ขอสิทธิ์ชื่อใหม่</div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 4, lineHeight: 1.8 }}>
            ใช้ชื่อเดิมซ้ำได้หลังหมดอายุหรือคืนสิทธิ์ แล้ว token จะเพิ่มขึ้นหนึ่งเสมอ
          </div>

          <div className="field">
            <label htmlFor="lock-name">ชื่อล็อก</label>
            <input
              id="lock-name"
              className="input mono"
              placeholder="report.render:2026-11"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <div className="dim" style={{ fontSize: 10.5, marginTop: 6, lineHeight: 1.7 }}>
              ตัวอักษรอังกฤษ ตัวเลข และ . _ : - เท่านั้น · เริ่มและจบด้วยตัวอักษรหรือตัวเลข · ไม่เกิน 120 ตัว
            </div>
          </div>

          <div className="field">
            <label htmlFor="lock-owner">ผู้ถือสิทธิ์</label>
            <input
              id="lock-owner"
              className="input"
              placeholder="เว้นว่างเพื่อใช้บัญชีที่ลงชื่อเข้าใช้"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
            />
            <div className="dim" style={{ fontSize: 10.5, marginTop: 6, lineHeight: 1.7 }}>
              ผู้ปฏิบัติงานควรส่งรหัสของตัวเอง เพื่อให้การรีสตาร์ตไม่ถูกมองว่าเป็นผู้แข่งขันรายใหม่
            </div>
          </div>

          <div className="field">
            <label htmlFor="lock-ttl">อายุสิทธิ์ (วินาที)</label>
            <input
              id="lock-ttl"
              className="input tnum"
              type="number"
              min={5}
              max={86400}
              value={ttl}
              onChange={(e) => setTtl(e.target.value)}
            />
          </div>

          <button
            className="btn btn-primary"
            style={{ marginTop: 18, width: '100%' }}
            disabled={!canAcquire}
            onClick={() => { setNotice(null); acquire.mutate() }}
          >
            {acquire.isPending ? 'กำลังขอสิทธิ์…' : 'ขอสิทธิ์และรับ token'}
          </button>

          {acquire.isError ? (
            <div className="form-error" style={{ marginTop: 12 }}>
              {errorText(acquire.error, 'ขอสิทธิ์ไม่สำเร็จ')}
            </div>
          ) : null}
          {notice ? (
            <div className="dim" style={{ fontSize: 12, marginTop: 12, lineHeight: 1.7 }}>{notice}</div>
          ) : null}
        </div>
      </div>

      <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.9, marginTop: 14 }}>
        token ถูกเก็บไว้แม้คืนสิทธิ์หรือหมดอายุ เพื่อไม่ให้ค่าที่เคยออกไปแล้วกลับมาใช้ซ้ำ ·
        การคืนสิทธิ์ต้องตรงทั้งผู้ถือและ token · ระบบจะปลดล็อกที่หมดอายุอัตโนมัติเมื่อตัวกวาดทำงาน
      </div>
    </>
  )
}
